import QRCode from "qrcode";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import brandMark from "../../shared/brand-mark.png";
import {
  AdminApiError,
  createPush,
  createOrder,
  importOrders,
  issueClaimToken,
  loadAnalytics,
  loadDashboard,
  loadOperations,
  loadOrders,
  loadPushJobs,
  type AdminPushInput,
  type AdminOrder,
  type AnalyticsDashboard,
  type ClaimTokenIssue,
  type Dashboard,
  type OperationsDashboard,
  type PushJob,
} from "./api";
import "./styles.css";

const orderStatus: Record<AdminOrder["status"], string> = {
  created: "待支付",
  paid: "已支付",
  claim_issued: "待领取",
  claimed: "已领取",
  refunded: "已退款",
  cancelled: "已取消",
};

function formatDate(value: string | null): string {
  if (!value) return "—";
  return new Intl.DateTimeFormat("zh-CN", { dateStyle: "medium", timeStyle: "short", hour12: false }).format(new Date(value));
}

function errorMessage(error: unknown): string {
  if (error instanceof AdminApiError) return `${error.message}（请求编号 ${error.requestId}）`;
  return "网络暂时不可用，请检查 API 与 Cloudflare Access 状态。";
}

function MetricCard({ icon, label, value, tone }: { icon: string; label: string; value: number; tone: string }) {
  return (
    <article className={`metric-card tone-${tone}`}>
      <span className="metric-icon" aria-hidden="true">{icon}</span>
      <div><p>{label}</p><strong>{value}</strong></div>
    </article>
  );
}

function IssueDrawer({ issue, onClose }: { issue: ClaimTokenIssue; onClose: () => void }) {
  const [qrSource, setQrSource] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    QRCode.toDataURL(issue.claimUrl, { width: 280, margin: 2, color: { dark: "#171114", light: "#fffefa" } })
      .then(setQrSource);
  }, [issue.claimUrl]);

  async function copyCode() {
    await navigator.clipboard.writeText(issue.humanCode);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  }

  return (
    <aside className="issue-drawer" aria-labelledby="issue-title">
      <button className="drawer-close" onClick={onClose} aria-label="关闭领取码抽屉">×</button>
      <div className="drawer-success" aria-hidden="true">✓</div>
      <h2 id="issue-title">领取码签发结果</h2>
      <p className="drawer-lead">领取码已经签发，请及时告知用户领取。</p>
      <div className="display-once" role="alert">原始领取信息仅本次显示。请妥善保存或及时传达给用户。</div>
      <section className="code-block">
        <span>一次性领取码</span>
        <strong>{issue.humanCode}</strong>
        <button onClick={copyCode}>{copied ? "已复制" : "复制领取码"}</button>
      </section>
      <dl className="issue-meta">
        <div><dt>知识库</dt><dd>{issue.knowledgeBaseName}</dd></div>
        <div><dt>有效期至</dt><dd>{formatDate(issue.expiresAt)}</dd></div>
      </dl>
      <section className="qr-block">
        <span>二维码（扫码领取）</span>
        {qrSource ? <img src={qrSource} alt="领取二维码" /> : <div className="qr-loading">正在生成二维码…</div>}
        {qrSource ? <a href={qrSource} download={`${issue.sourceOrderId}-领取二维码.png`}>下载二维码</a> : null}
      </section>
      <p className="drawer-note">此码为一次性使用，任一形式领取后立即失效，请勿泄露给他人。</p>
    </aside>
  );
}

function NewOrderDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (order: AdminOrder) => void }) {
  const [sourceOrderId, setSourceOrderId] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!sourceOrderId.trim() || busy) return;
    setBusy(true);
    setError("");
    try {
      onCreated(await createOrder({ sourceOrderId: sourceOrderId.trim(), ...(phone ? { phone } : {}) }));
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="dialog-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="order-dialog" role="dialog" aria-modal="true" aria-labelledby="new-order-title">
        <button className="drawer-close" onClick={onClose} aria-label="关闭新建订单">×</button>
        <p className="eyebrow">手工订单</p>
        <h2 id="new-order-title">新建已支付订单</h2>
        <p>首发知识库固定为 AI 工具与效率知识库，价格 66 元／年。</p>
        {error ? <div className="admin-error" role="alert">{error}</div> : null}
        <form onSubmit={submit}>
          <label>小红书订单号<input value={sourceOrderId} onChange={(event) => setSourceOrderId(event.target.value)} required autoFocus /></label>
          <label>订单手机号（可选）<input value={phone} onChange={(event) => setPhone(event.target.value.replace(/\D/gu, "").slice(0, 11))} inputMode="tel" placeholder="未提供时由首次验证手机号绑定" /></label>
          <div className="dialog-actions"><button type="button" onClick={onClose}>取消</button><button className="solid-button" disabled={!sourceOrderId.trim() || busy}>{busy ? "正在创建…" : "创建订单"}</button></div>
        </form>
      </section>
    </div>
  );
}

const pushStatusLabels: Record<PushJob["status"], string> = {
  scheduled: "已定时",
  queued: "排队中",
  processing: "发送中",
  completed: "已完成",
  partial: "部分成功",
  failed: "失败",
  cancelled: "已取消",
};

function PushComposer({ onSent }: { onSent: () => Promise<void> }) {
  const [draft, setDraft] = useState<AdminPushInput>({
    kind: "breaking",
    title: "",
    body: "",
    audience: "knowledge_base",
    knowledgeBaseId: "ai-tools-efficiency",
    reason: "",
  });
  const [previewing, setPreviewing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  function update<K extends keyof AdminPushInput>(key: K, value: AdminPushInput[K]) {
    setPreviewing(false);
    setDraft((current) => ({ ...current, [key]: value }));
  }

  function changeAudience(audience: AdminPushInput["audience"]) {
    setPreviewing(false);
    setDraft((current) => ({
      ...current,
      audience,
      ...(audience === "knowledge_base" ? { knowledgeBaseId: "ai-tools-efficiency" } : {}),
      ...(audience !== "knowledge_base" ? { knowledgeBaseId: undefined } : {}),
      ...(audience !== "test_device" ? { targetDeviceId: undefined } : {}),
    }));
  }

  const valid = draft.title.trim().length > 0
    && draft.body.trim().length > 0
    && draft.reason.trim().length >= 2
    && (draft.audience !== "test_device" || Boolean(draft.targetDeviceId));

  async function confirmSend() {
    if (!valid || busy) return;
    setBusy(true);
    setError("");
    try {
      await createPush(draft);
      setPreviewing(false);
      setDraft((current) => ({ ...current, title: "", body: "", contentId: undefined, route: undefined, reason: "" }));
      await onSent();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="release-panel push-composer" id="push" aria-labelledby="push-title">
      <div className="section-heading"><div><p className="eyebrow">受控发布</p><h2 id="push-title">推送发布台</h2></div><span className="guardrail">发送前强制预览</span></div>
      {error ? <div className="admin-error" role="alert">{error}</div> : null}
      <div className="composer-grid">
        <form onSubmit={(event) => { event.preventDefault(); if (valid) setPreviewing(true); }}>
          <div className="form-pair">
            <label>类型<select value={draft.kind} onChange={(event) => update("kind", event.target.value as AdminPushInput["kind"])}><option value="daily_digest">每日摘要</option><option value="breaking">重要更新</option><option value="subscription_expiring">订阅到期</option><option value="security">安全通知</option><option value="support">客服进展</option><option value="test">测试推送</option></select></label>
            <label>受众<select value={draft.audience} onChange={(event) => changeAudience(event.target.value as AdminPushInput["audience"])}><option value="knowledge_base">首发知识库订阅者</option><option value="test_device">单台测试设备</option><option value="all">全部已登记设备</option></select></label>
          </div>
          {draft.audience === "test_device" ? <label>测试设备 ID<input value={draft.targetDeviceId ?? ""} onChange={(event) => update("targetDeviceId", event.target.value)} placeholder="设备 UUID" required /></label> : null}
          <label>标题<input aria-label="标题" value={draft.title} onChange={(event) => update("title", event.target.value.slice(0, 80))} maxLength={80} required /><small>{draft.title.length}/80</small></label>
          <label>正文<textarea aria-label="正文" value={draft.body} onChange={(event) => update("body", event.target.value.slice(0, 240))} maxLength={240} rows={4} required /><small>{draft.body.length}/240</small></label>
          <div className="form-pair">
            <label>内容 ID（可选）<input value={draft.contentId ?? ""} onChange={(event) => { const value = event.target.value; update("contentId", value || undefined); update("route", value ? `knowledgehammer://content/${value}` : undefined); }} /></label>
            <label>发送时间（可选）<input type="datetime-local" onChange={(event) => update("scheduledAt", event.target.value ? new Date(event.target.value).toISOString() : undefined)} /></label>
          </div>
          <label>操作原因<input aria-label="操作原因" value={draft.reason} onChange={(event) => update("reason", event.target.value.slice(0, 500))} placeholder="将写入不可变审计日志" required /></label>
          <button className="solid-button" disabled={!valid}>预览推送</button>
        </form>
        <aside className="notification-preview" aria-live="polite">
          <p className="preview-label">推送预览</p>
          <div className="phone-preview">
            <div className="notification-card"><div className="notification-app"><img src={brandMark} alt="" /><strong>知识锤子</strong><span>现在</span></div><h3>{draft.title || "推送标题"}</h3><p>{draft.body || "这里显示用户将在系统通知中看到的正文。"}</p></div>
          </div>
          <dl className="preview-meta"><div><dt>受众</dt><dd>{draft.audience === "test_device" ? "单台测试设备" : draft.audience === "all" ? "全部设备" : "首发知识库"}</dd></div><div><dt>深链</dt><dd>{draft.route ?? "无"}</dd></div></dl>
          {previewing ? <div className="confirm-strip" role="alert"><strong>请再次确认受众与文案</strong><p>确认后任务将进入 Queue，并留下管理员与原因审计记录。</p><button className="danger-button" onClick={() => void confirmSend()} disabled={busy}>{busy ? "正在提交…" : "确认创建推送任务"}</button></div> : <p className="preview-hint">填写完成后点击“预览推送”，这里会出现最终确认操作。</p>}
        </aside>
      </div>
    </section>
  );
}

function AnalyticsPanel({ analytics }: { analytics: AnalyticsDashboard | null }) {
  const maxValue = Math.max(1, ...((analytics?.series ?? []).map((item) => item.value)));
  return (
    <section className="release-panel" id="analytics" aria-labelledby="analytics-title">
      <div className="section-heading"><div><p className="eyebrow">自有数据</p><h2 id="analytics-title">隐私最小化分析</h2></div><span className="privacy-badge">不跟踪 · 不含搜索全文</span></div>
      <div className="insight-metrics"><MetricCard icon="◎" label="活跃安装" value={analytics?.activeInstallations ?? 0} tone="neutral" /><MetricCard icon="▱" label="文章打开" value={analytics?.totals.article_opened ?? 0} tone="success" /><MetricCard icon="◉" label="音频开始" value={analytics?.totals.audio_started ?? 0} tone="warning" /><MetricCard icon="∅" label="搜索无结果" value={analytics?.diagnosticCounts.search_no_result ?? 0} tone="danger" /></div>
      <div className="analytics-grid">
        <div className="chart-card"><h3>近 {analytics?.range.days ?? 14} 天事件趋势</h3><div className="mini-chart">{(analytics?.series ?? []).slice(-28).map((item, index) => <span key={`${item.date}-${item.eventName}-${index}`} style={{ height: `${Math.max(8, item.value / maxValue * 100)}%` }} title={`${item.date} · ${item.eventName}: ${item.value}`} />)}{!analytics?.series.length ? <p>收到灰度事件后显示趋势。</p> : null}</div></div>
        <div className="privacy-contract"><h3>数据边界</h3><ul><li>原始事件保留 {analytics?.privacy.retentionDays ?? 90} 天</li><li>安装标识仅保存单向哈希</li><li>不上传手机号、正文、完整搜索词或验证码</li><li>无广告 SDK、无 ATT、无跨 App 跟踪</li></ul></div>
      </div>
    </section>
  );
}

function OperationsPanel({ operations, jobs }: { operations: OperationsDashboard | null; jobs: PushJob[] }) {
  return (
    <section className="release-panel" id="operations" aria-labelledby="operations-title">
      <div className="section-heading"><div><p className="eyebrow">slofst.com · {operations?.environment ?? "staging"}</p><h2 id="operations-title">运行与审计</h2></div><span className="guardrail">审计日志只追加</span></div>
      <div className="health-grid">{["worker", "d1", "r2", "queue", "apns", "scheduled"].map((component) => { const check = operations?.checks.find((item) => item.component === component); return <article className={`health-card health-${check?.status ?? "unknown"}`} key={component}><span aria-hidden="true">{check?.status === "healthy" ? "✓" : check?.status === "failed" ? "!" : "·"}</span><div><strong>{component.toUpperCase()}</strong><p>{check ? `${check.status} · ${check.latencyMs ?? "—"} ms` : "等待首次定时健康检查"}</p></div></article>; })}</div>
      <div className="operations-grid"><div><h3>最近推送任务</h3><div className="compact-table">{jobs.slice(0, 6).map((job) => <div key={job.id}><span className={`status status-${job.status}`}>{pushStatusLabels[job.status]}</span><strong>{job.title}</strong><small>{job.success_count}/{job.recipient_count} 送达 · {formatDate(job.scheduled_at)}</small></div>)}{jobs.length === 0 ? <p>尚无推送任务。</p> : null}</div></div><div className="operations-summary"><h3>当前控制面</h3><dl><div><dt>活跃 APNs Token</dt><dd>{operations?.push.activeTokens ?? 0}</dd></div><div><dt>Queue 积压</dt><dd>{operations?.queue.backlogCount ?? "不可用"}</dd></div><div><dt>审计条目</dt><dd>{operations?.audit.entries ?? 0}</dd></div><div><dt>异常 Token</dt><dd>{operations?.push.failingTokens ?? 0}</dd></div></dl></div></div>
    </section>
  );
}

export default function App() {
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [orders, setOrders] = useState<AdminOrder[]>([]);
  const [analytics, setAnalytics] = useState<AnalyticsDashboard | null>(null);
  const [operations, setOperations] = useState<OperationsDashboard | null>(null);
  const [pushJobs, setPushJobs] = useState<PushJob[]>([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [issuingId, setIssuingId] = useState<string | null>(null);
  const [issue, setIssue] = useState<ClaimTokenIssue | null>(null);
  const [newOrderOpen, setNewOrderOpen] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [nextDashboard, nextOrders, nextAnalytics, nextOperations, nextPushJobs] = await Promise.all([
        loadDashboard(),
        loadOrders(),
        loadAnalytics(),
        loadOperations(),
        loadPushJobs(),
      ]);
      setDashboard(nextDashboard);
      setOrders(nextOrders);
      setAnalytics(nextAnalytics);
      setOperations(nextOperations);
      setPushJobs(nextPushJobs);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const filteredOrders = useMemo(() => {
    const keyword = filter.trim().toLowerCase();
    if (!keyword) return orders;
    return orders.filter((order) => `${order.sourceOrderId} ${order.knowledgeBaseName}`.toLowerCase().includes(keyword));
  }, [filter, orders]);

  async function issueFor(order: AdminOrder) {
    setIssuingId(order.id);
    setError("");
    try {
      setIssue(await issueClaimToken(order.id));
      await refresh();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setIssuingId(null);
    }
  }

  async function uploadCsv(file: File) {
    setError("");
    try {
      const result = await importOrders(file);
      if (result.rejectedRows) setError(`导入完成：成功 ${result.acceptedRows} 条，失败 ${result.rejectedRows} 条。`);
      await refresh();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      if (fileInput.current) fileInput.current.value = "";
    }
  }

  return (
    <div className="admin-shell">
      <aside className="sidebar">
        <div className="admin-brand"><img src={brandMark} alt="" /><strong>知识锤子</strong><span>管理后台</span></div>
        <nav aria-label="后台主导航">
          <a href="#overview"><span>⌂</span>发布概览</a>
          <a href="#orders"><span>▤</span>订单与领取</a>
          <a href="#analytics"><span>◎</span>产品分析</a>
          <a href="#push"><span>↗</span>推送发布</a>
          <a href="#operations"><span>◇</span>运行与审计</a>
        </nav>
        <div className="access-card"><span>✓</span><div><strong>Cloudflare Access</strong><p>身份凭证与后台白名单双重校验</p></div></div>
      </aside>

      <main className="admin-main">
        <header className="page-header" id="overview">
          <div><p className="eyebrow">Phase 4 · 发布与监控</p><h1>发布控制台</h1><p>从订单履约到灰度推送、隐私分析和运行健康的一体化控制面</p></div>
          <div className="header-actions">
            <input ref={fileInput} type="file" accept=".csv,text/csv" hidden onChange={(event) => event.target.files?.[0] && void uploadCsv(event.target.files[0])} />
            <button className="outline-button" onClick={() => fileInput.current?.click()}>⇧ 导入 CSV</button>
            <button className="solid-button" onClick={() => setNewOrderOpen(true)}>＋ 新建订单</button>
          </div>
        </header>

        {error ? <div className="admin-error" role="alert">{error}</div> : null}

        <section className="metrics" aria-label="订单关键指标">
          <MetricCard icon="◷" label="待领取" value={dashboard?.pendingClaims ?? 0} tone="neutral" />
          <MetricCard icon="✓" label="今日领取" value={dashboard?.claimedToday ?? 0} tone="success" />
          <MetricCard icon="⌛" label="即将过期" value={dashboard?.expiringSoon ?? 0} tone="warning" />
          <MetricCard icon="!" label="异常订单" value={dashboard?.exceptionalOrders ?? 0} tone="danger" />
        </section>

        <section className="order-panel" id="orders" aria-busy={loading} aria-labelledby="orders-title">
          <div className="section-heading compact-heading"><div><p className="eyebrow">订单履约</p><h2 id="orders-title">订单与领取</h2></div></div>
          <div className="table-tools">
            <label className="search-box"><span aria-hidden="true">⌕</span><input value={filter} onChange={(event) => setFilter(event.target.value)} placeholder="搜索订单号、知识库名称" aria-label="搜索订单" /></label>
            <span>共 {filteredOrders.length} 条</span>
          </div>
          <div className="table-scroll">
            <table>
              <thead><tr><th>订单号</th><th>平台</th><th>知识库</th><th>订单状态</th><th>领取状态</th><th>创建时间</th><th>操作</th></tr></thead>
              <tbody>
                {filteredOrders.map((order) => (
                  <tr key={order.id}>
                    <td><strong>{order.sourceOrderId}</strong></td>
                    <td><span className="platform-pill">小红书</span></td>
                    <td>{order.knowledgeBaseName}</td>
                    <td><span className={`status status-${order.status}`}>{orderStatus[order.status]}</span></td>
                    <td>{order.claimStatus === "not_issued" ? "未签发" : order.claimStatus === "redeemed" ? "已领取" : order.status === "claim_issued" ? "待领取" : order.claimStatus}</td>
                    <td>{formatDate(order.createdAt)}</td>
                    <td>
                      {["paid", "claim_issued"].includes(order.status) ? (
                        <button className="table-action" disabled={issuingId === order.id} onClick={() => void issueFor(order)}>{issuingId === order.id ? "签发中…" : order.status === "claim_issued" ? "重新签发" : "签发领取码"}</button>
                      ) : <button className="table-action muted" disabled>查看</button>}
                    </td>
                  </tr>
                ))}
                {!loading && filteredOrders.length === 0 ? <tr><td colSpan={7} className="empty-table">暂无订单。你可以新建订单或导入 CSV。</td></tr> : null}
              </tbody>
            </table>
          </div>
        </section>

        <AnalyticsPanel analytics={analytics} />
        <PushComposer onSent={refresh} />
        <OperationsPanel operations={operations} jobs={pushJobs} />

        <footer className="audit-footer">◇ 审计提示：订单、领取、权益与推送发布均记录管理员哈希、请求编号、原因与时间；历史记录不可改写或删除。</footer>
      </main>

      {issue ? <IssueDrawer issue={issue} onClose={() => setIssue(null)} /> : null}
      {newOrderOpen ? <NewOrderDialog onClose={() => setNewOrderOpen(false)} onCreated={(order) => { setOrders((current) => [order, ...current]); setNewOrderOpen(false); void refresh(); }} /> : null}
    </div>
  );
}
